/**
 * Error sanitization utilities
 *
 * Strips Convex internal wrapper text ("Called by client", function paths, etc.)
 * from error messages before showing them to users.
 *
 * Usage:
 *   import { friendlyError } from "@/lib/utils/error-utils";
 *   toast.error(friendlyError(error));
 */

// Patterns that indicate Convex internal plumbing — never show these to users
const CONVEX_NOISE_PATTERNS = [
    /^\[CONVEX [^\]]*\]\s*/,
    /Server Error\s*/,
    /^Uncaught ConvexError:\s*/,
    /^Uncaught Error:\s*/,
    /^Error:\s*/,
    /Called by client/gi,
    /\[Request ID:[^\]]+\]/gi,
    /at async.*convex.*/gi,
    /convex:\/\//gi,
];

// Generic user-friendly fallback when we have nothing better to say
const GENERIC_ERROR = "Something went wrong. Please try again.";

/**
 * Map known technical substrings to human-readable messages.
 * Add entries here as new patterns emerge.
 */
const KNOWN_ERRORS: Array<{ match: RegExp | string; message: string }> = [
    { match: /insufficient credits/i, message: "You don't have enough credits for this action." },
    { match: /credit/i, message: "A credits issue occurred. Please check your balance." },
    { match: /overloaded|503|429|rate.?limit|quota|RESOURCE_EXHAUSTED/i, message: "The AI service is busy right now. Please try again in a moment." },
    { match: /5\d{2}|internal.?server|INTERNAL/i, message: "Something went wrong on our end. Please try again shortly." },
    { match: /api key|not configured/i, message: "The AI service is not configured. Please contact support." },
    { match: /not found/i, message: "The requested item could not be found." },
    { match: /unauthorized|not authorized|permission/i, message: "You don't have permission to do that." },
    { match: /network|fetch|ECONNREFUSED/i, message: "Network error. Please check your connection and try again." },
    { match: /timeout/i, message: "The request timed out. Please try again." },
    { match: /storage.*full|storage.*exceeded/i, message: "Your storage is full. Please upgrade your plan." },
    // ── Google Veo / Gemini Responsible-AI content filter ──────────────
    // The provider's RAI layer rejects prompts whose IMAGE or TEXT
    // implies a child or specific person, even when the brand is just
    // marketing kids' apparel. Our prompt builder + Veo config tell the
    // model not to render people at all, but this error can still
    // trigger when the source image shows a child very prominently.
    // Surface an actionable hint instead of the raw provider text.
    {
        match: /raiMediaFilteredCount|content was filtered|safety polic|containing children|child(?:ren)?|minor|policy.*violat/i,
        message:
            "The AI couldn't generate a video from this image (its safety filter flagged the wearer). Try a flat-lay or mannequin photo of the garment without a model — that's the most reliable way to generate marketing videos for clothing.",
    },
];

/**
 * Returns a user-friendly error message from any thrown error object.
 * Strips Convex internals and maps known patterns to clear messages.
 */
export function friendlyError(error: unknown, fallback = GENERIC_ERROR): string {
    // A ConvexError carries the one sentence the server meant the user to
    // read as `data` — use it before any pattern matching mangles it.
    const data = (error as any)?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data && typeof data === "object" && typeof (data as any).message === "string" && (data as any).message.trim()) {
        return (data as any).message.trim();
    }

    let raw =
        error instanceof Error
            ? error.message
            : typeof error === "string"
                ? error
                : fallback;

    // "…Server Error\nUncaught ConvexError: <the message>\n  at …" — keep the message.
    const thrown = raw.match(/Uncaught (?:ConvexError|Error):\s*([^\n]+)/);
    if (thrown) raw = thrown[1];

    // Strip Convex noise prefixes
    for (const pattern of CONVEX_NOISE_PATTERNS) {
        raw = raw.replace(pattern, "").trim();
    }

    // Match known error patterns first
    for (const { match, message } of KNOWN_ERRORS) {
        if (typeof match === "string" ? raw.includes(match) : match.test(raw)) {
            return message;
        }
    }

    // If what remains looks like a sensible user-facing message (short, no stack info), use it
    const cleaned = raw.split("\n")[0].trim();
    if (cleaned.length > 0 && cleaned.length < 200 && !cleaned.includes("at ")) {
        return cleaned;
    }

    return fallback;
}
